/**
 * CrmPushEditor
 *
 * Workflow node that explicitly pushes data to a CRM at a chosen point
 * in the workflow (e.g. after a lead-qualification question sequence).
 *
 * Supported CRMs: HubSpot (Dynamics planned)
 *
 * Unlike the auto-push that fires on every conversation resolve (HubSpotConfig.PushTranscripts),
 * this node gives the workflow author explicit control:
 *   • WHEN to push (mid-flow or end-of-flow)
 *   • WHAT to push (transcript, custom note, or a variable-substituted message)
 *   • WHO to push to (defaults to conversation contact; can override with a variable)
 */
import * as React from "react";
import { Link } from "react-router-dom";
import { FormGroup, ToggleGroup } from "../RightAsideHelpers";
import Select from "@/components/Select";
import { WorkflowNode } from "../../types";
import useHubSpotConfigs from "@/hooks/useHubSpotConfigs";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

const CRM_TYPE_OPTIONS = [
  { value: "hubspot",  label: "HubSpot" }
];

export default function CrmPushEditor({ node, onUpdate }: Props) {
  const { items: hubSpotConfigs } = useHubSpotConfigs();

  const crmType = node.crmPushType ?? "hubspot";
  const includeTranscript = node.crmPushIncludeTranscript ?? true;

  const hubSpotOptions = [
    { value: 0, label: "— Select HubSpot connection —" },
    ...hubSpotConfigs.map(c => ({ value: c.id, label: c.displayName || `HubSpot #${c.id}` }))
  ];

  return (
    <>
      <div className="mb-3 rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800">
        Pushes a note or transcript to a CRM contact at this point in the workflow.
        The contact is matched by email — defaults to the conversation contact's email.
      </div>

      <FormGroup label="CRM">
        <Select
          options={CRM_TYPE_OPTIONS}
          value={crmType}
          onChange={value => onUpdate({ crmPushType: value as string })}
        />
      </FormGroup>

      {crmType === "hubspot" && (
        <FormGroup label="HubSpot Connection">
          {hubSpotConfigs.length === 0 ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              No HubSpot connection configured.{" "}
              <Link to="/integrations/hubspot" className="underline font-medium">Connect HubSpot →</Link>
            </p>
          ) : (
            <Select
              options={hubSpotOptions}
              value={node.crmPushConfigId ?? 0}
              onChange={value => onUpdate({ crmPushConfigId: Number(value) || undefined })}
            />
          )}
        </FormGroup>
      )}

      <FormGroup label="Contact Email (optional)">
        <input
          className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          value={node.crmPushContactEmail ?? ""}
          onChange={e => onUpdate({ crmPushContactEmail: e.currentTarget.value })}
          placeholder="Leave empty to use conversation contact — or <<contact.email>>"
        />
        <p className="text-xs text-slate-400 mt-1">
          Leave empty to use the conversation contact's email automatically.
        </p>
      </FormGroup>

      <ToggleGroup
        label="Include Full Transcript"
        toggled={includeTranscript}
        changeToggle={() => onUpdate({ crmPushIncludeTranscript: !includeTranscript })}
        helpText="When on, the full chat transcript is pushed as the note body"
      />

      {!includeTranscript && (
        <FormGroup label="Note Text">
          <textarea
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={node.crmPushNoteText ?? ""}
            onChange={e => onUpdate({ crmPushNoteText: e.currentTarget.value })}
            placeholder="Note text to push. Supports <<variable>> substitution."
            rows={4}
          />
          <p className="text-xs text-slate-400 mt-1">
            Available: <code>{"<<contact.email>>"}</code> <code>{"<<contact.name>>"}</code>{" "}
            <code>{"<<conversation.id>>"}</code>
          </p>
        </FormGroup>
      )}
    </>
  );
}
